/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useEmblaCarousel } from 'embla-carousel/react';
import { defineMessages, useIntl } from 'react-intl';

import {
  CarouselWrapper,
  CarouselContainer,
  Slide,
  SlideInner,
  SlideImg,
  SlideCaption,
  viewportStyle,
  ThumbContainer,
} from './Carousel.styles';

import Thumb from './Thumb';

const messages = defineMessages({
  carouselAriaRoleDescription: {
    defaultMessage: "Gallerie d'images",
    description: 'Aria role description of the carousel',
  },
  carouselSlideAriaRoleDescription: {
    defaultMessage: 'Diapositive',
    description: 'Aria role description of a carousel slide',
  },
  carouselSlideAriaLabel: {
    defaultMessage: 'sur',
    description: 'Aria label of a carousel slide',
  },
});

const Carousel = ({ slides, ...rest }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaCarouselReactRef, embla] = useEmblaCarousel();
  const [emblaCarouselReactThumbsRef, emblaThumbs] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    selectedClass: '',
    draggable: false,
    loop: true,
    align: 'start',
  });

  const { formatMessage } = useIntl();

  const onThumbClick = useCallback(
    (index) => {
      if (!embla || !emblaThumbs) return;
      if (emblaThumbs.clickAllowed()) embla.scrollTo(index);
    },
    [embla, emblaThumbs]
  );

  const onSelect = useCallback(() => {
    if (!embla || !emblaThumbs) return;
    setSelectedIndex(embla.selectedScrollSnap());
    emblaThumbs.scrollTo(embla.selectedScrollSnap());
  }, [embla, emblaThumbs, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on('select', onSelect);
  }, [embla, onSelect]);

  return (
    <CarouselWrapper
      role='region'
      aria-roledescription={formatMessage(messages.carouselAriaRoleDescription)}
      {...rest}
    >
      <div css={viewportStyle} ref={emblaCarouselReactRef}>
        <CarouselContainer aria-live='polite'>
          {slides.map((slide, index) => (
            <Slide
              id={slide.key}
              key={slide.key}
              aria-label={`${formatMessage(
                messages.carouselSlideAriaRoleDescription
              )} ${index + 1} ${formatMessage(
                messages.carouselSlideAriaLabel
              )} ${slides.length}`}
            >
              <SlideInner>
                <SlideImg fluid={slide.sourcePicture} alt={slide.alt} />

                <SlideCaption>{slide.caption}</SlideCaption>
              </SlideInner>
            </Slide>
          ))}
        </CarouselContainer>
      </div>

      <div css={viewportStyle} ref={emblaCarouselReactThumbsRef}>
        <ThumbContainer>
          {slides.map((slide, index) => (
            <Thumb
              key={`${slide.key}-t`}
              selected={index === selectedIndex}
              slide={slide}
              onClick={() => onThumbClick(index)}
            />
          ))}
        </ThumbContainer>
      </div>
    </CarouselWrapper>
  );
};

Carousel.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      caption: PropTypes.string.isRequired,
      sourcePicture: PropTypes.arrayOf(
        PropTypes.shape({
          aspectRatio: PropTypes.number.isRequired,
          src: PropTypes.string.isRequired,
          srcSet: PropTypes.string.isRequired,
          srcSetWebp: PropTypes.string.isRequired,
          srcWebp: PropTypes.string.isRequired,
        })
      ).isRequired,
      sourceThumbnail: PropTypes.arrayOf(
        PropTypes.shape({
          aspectRatio: PropTypes.number.isRequired,
          src: PropTypes.string.isRequired,
          srcSet: PropTypes.string.isRequired,
          srcSetWebp: PropTypes.string.isRequired,
          srcWebp: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
};

export default Carousel;
